import React from "react";

import { ColDef, RowDoubleClickedEvent, SelectionChangedEvent } from "ag-grid-community";
import BaseGrid from "../../../components/BaseGrid";

import { DeviceGridItem } from "../data/Device";
import { useDevicesContext } from "../context/DevicesContext";
import { useDeviceDrawerStore } from "../store/DeviceDrawerStore";
import { useDeviceGridStore } from "../store/DeviceGridStore";

import { DateTime } from "luxon";

const dateFormat = (data: any): string => {
    return data.value ? DateTime.fromISO(data.value as string).toFormat("dd/MM/yyyy") : "N/A";
}

export default function DeviceGrid() {
    const { devices, isDevicesFetching, listOptions, isListOptionFetching } = useDevicesContext();
    const { openEdit } = useDeviceDrawerStore();
    const { setSelectedDevices } = useDeviceGridStore();

    const getOptionName = React.useCallback((categoryId: number, optionId?: number): string => {
        if (!optionId) return "N/A";
        const list = listOptions?.find(o => o.categoryId === categoryId);
        if (list) {
            const option = list.options.find(o => o.id === optionId);
            return option?.name ?? "N/A";
        }
        return "N/A";
    }, [listOptions]);

    const gridData = React.useMemo(() => {
        return devices?.map((device) => {
            return {
                ...device,
                price: device.price ?? 0,
                ownerValue: getOptionName(1, device.owner),
                typeValue: getOptionName(2, device.type),
                vendorValue: getOptionName(3, device.vendor),
                manufacturerValue: getOptionName(4, device.manufacturer),
                modelValue: getOptionName(5, device.model),
                integrityValue: getOptionName(6, device.integrity),
                confidentialityValue: getOptionName(7, device.confidentiality),
                availabilityValue: getOptionName(8, device.availability),
                statusValue: getOptionName(9, device.status),
                locationValue: getOptionName(10, device.location),
            };
        }) ?? [] as DeviceGridItem[];
    }, [devices, getOptionName]);

    const colDefs = React.useMemo<ColDef[]>(() => {
        return [
            {
                field: "id",
                headerName: "ID",
                filter: true,
                minWidth: 95,
                maxWidth: 105,
                pinned: "left"
            },
            {
                field: "label",
                headerName: "Label",
                minWidth: 120,
                pinned: "left"
            },
            {
                field: "serialNumber",
                headerName: "Serial Number",
                minWidth: 150,
            },
            {
                field: "ownerValue",
                headerName: "Owner",
                minWidth: 100,
            },
            {
                field: "typeValue",
                headerName: "Type",
                minWidth: 100,
            },
            {
                field: "vendorValue",
                headerName: "Vendor",
                minWidth: 100,
            },
            {
                field: "manufacturerValue",
                headerName: "Manufacturer",
                minWidth: 150,
            },
            {
                field: "modelValue",
                headerName: "Model",
                minWidth: 100,
            },
            {
                field: "integrityValue",
                headerName: "Integrity",
                minWidth: 140,
            },
            {
                field: "confidentialityValue",
                headerName: "Confidentiality",
                minWidth: 150,
            },
            {
                field: "availabilityValue",
                headerName: "Availability",
                minWidth: 140,
            },
            {
                field: "statusValue",
                headerName: "Status",
                minWidth: 100,
            },
            {
                field: "locationValue",
                headerName: "Location",
                minWidth: 100,
            },
            {
                field: "cpu",
                headerName: "CPU",
                minWidth: 100,
            },
            {
                field: "memory",
                headerName: "Memory",
                minWidth: 120,
            },
            {
                field: "disk",
                headerName: "Disk",
                minWidth: 100,
            },
            {
                field: "price",
                headerName: "Price",
                minWidth: 100,
                valueFormatter: (params) => '$ ' + params.value,
            },
            {
                field: "purchaseDate",
                headerName: "Purchase Date",
                minWidth: 150,
                valueFormatter: dateFormat,
            },
            {
                field: "isWarranty",
                headerName: "Warranty",
                minWidth: 120,
            },
            {
                field: "warrantyDate",
                headerName: "Warranty Date",
                minWidth: 200,
                valueGetter: (params) => {
                    return params.data.isWarranty ? DateTime.fromISO(params.data.warrantyDate).toFormat("dd/MM/yyyy") : "N/A";
                }
            },
            {
                field: "lifeTime",
                headerName: "Life Time",
                minWidth: 140,
            },
            {
                field: "laptopBag",
                headerName: "Laptop Bag",
                minWidth: 140,
            },
        ];
    }, []);

    const handleRowDoubleClick = React.useCallback((e: RowDoubleClickedEvent) => {
        const device = e.data as DeviceGridItem;
        openEdit(device);
    }, [openEdit]);

    const onSelectionChanged = React.useCallback((e: SelectionChangedEvent) => {
        setSelectedDevices(e.api.getSelectedRows() as DeviceGridItem[]);
    }, [setSelectedDevices]);

    const onReset = React.useCallback(() => {
        setSelectedDevices([]);
    }, [setSelectedDevices]);

    return (
        <BaseGrid<DeviceGridItem>
            isSingle={false}
            isLoading={isDevicesFetching || isListOptionFetching}
            data={gridData}
            colDefs={colDefs}
            onRowDoubleClick={handleRowDoubleClick}
            onSelectionChanged={onSelectionChanged}
            onReset={onReset}
        />
    );
}
